/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Putting text on the clipboard, and saying honestly whether it worked.
 *
 * Extracted from the client's channel band so the action bar beside it runs
 * the same code: two copy buttons on one screen that disagree about whether
 * a copy succeeded is a bug waiting for the first browser that refuses one
 * of them.
 *
 * The promise never rejects. A caller's job is to tell the person what
 * happened, and a rejection would make "say it failed" the exceptional path
 * rather than an answer — which is how silent failures get written.
 */

/**
 * Copies text, falling back to a detached textarea.
 *
 * `navigator.clipboard` is undefined outside a secure context, and the local
 * stack is served over plain http on some ports — without the fallback the
 * click would do nothing at all there, silently. It also rejects when the
 * window is not focused, which is the common case behind an automation or a
 * background tab.
 *
 * @param value - The text to place on the clipboard.
 * @returns Whether the copy actually happened.
 */
export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return copyViaTextarea(value);
  }
}

/**
 * Legacy clipboard path for non-secure contexts.
 *
 * @param value - The text to place on the clipboard.
 * @returns Whether the copy succeeded.
 */
function copyViaTextarea(value: string): boolean {
  try {
    const area = document.createElement('textarea');
    area.value = value;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch {
    /* execCommand is gone in some engines; that is a failed copy, not a crash. */
    return false;
  }
}
